import { useEffect, useRef, useState } from 'react';
import { groupBySlot, pacing, toMinutes } from '../../lib/bookingQueue';
import BookingCard from './BookingCard';
import WaitlistPanel from './WaitlistPanel';

// The service queue — the Bookings tab's default view. One time-ordered track for the
// selected day with every booking on it, table or not, grouped by half-hour slot. Past
// slots fade, the now-line sits between the slot that's gone and the one that's next,
// and the waitlist joins the same track at the bottom: a seated walk-in is just another
// card in its slot.

const BookingQueue = ({
  reservations, allReservations, tables, isToday, currentTime, lateAfter, selectedDate,
  suggestions, onSuggest, onAcceptSuggestion, onEdit, onChanged,
}) => {
  const nowMinutes = isToday ? toMinutes(currentTime) : null;
  const ctx = { nowMinutes, isToday, lateAfter };
  const groups = groupBySlot(reservations, ctx);
  // Pacing reads the whole day, not a search's slice of it.
  const dayRows = allReservations || reservations;
  const [openId, setOpenId] = useState(null);
  const nowRef = useRef(null);

  // Bring the now-line into view when the day loads.
  useEffect(() => {
    if (isToday && nowRef.current) {
      const t = setTimeout(() => nowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isToday, selectedDate]);

  // Where the now-line goes: before the first group whose slot is still ahead.
  let nowPlaced = !isToday || nowMinutes == null;

  return (
    <div className="space-y-1" data-testid="booking-queue">
      {groups.length === 0 && (
        <div className="rounded-xl border bg-white py-12 text-center text-sm text-ink-faint" data-testid="queue-empty">
          No bookings {isToday ? 'today' : 'on this day'} yet.
        </div>
      )}

      {groups.map((g) => {
        const slotMins = toMinutes(g.slot);
        const past = isToday && nowMinutes != null && slotMins + 30 <= nowMinutes;
        let line = null;
        if (!nowPlaced && slotMins + 30 > nowMinutes) {
          nowPlaced = true;
          line = (
            <div ref={nowRef} className="flex items-center gap-3 py-1" data-testid="queue-now">
              <span className="w-14 shrink-0 text-right font-mono text-[11px] font-bold text-signal-stop tabular-nums">{currentTime}</span>
              <span className="flex-1 border-t-2 border-dashed border-signal-stop-line" />
            </div>
          );
        }
        return (
          <div key={g.slot}>
            {line}
            <div className={`flex gap-3 py-2 ${past ? 'opacity-60' : ''}`} data-testid={`queue-slot-${g.slot}`}>
              <div className="w-14 shrink-0 text-right pt-3">
                <span className="font-mono text-[13px] font-semibold text-ink-soft tabular-nums">{g.slot}</span>
                <div className="text-[10px] text-ink-faint">{g.items.reduce((n, i) => n + (i.r.party_size || 0), 0)} cov</div>
              </div>
              <div className="flex-1 min-w-0">
                {(() => {
                  const p = pacing(g.slot, dayRows, tables, ctx);
                  const tight = p.free === 0 && p.freeing === 0;
                  return (
                    <div className={`mb-1.5 flex items-center gap-2 text-[11px] ${tight ? 'text-signal-stop' : 'text-ink-faint'}`} data-testid={`pacing-${g.slot}`}>
                      <span><b className="font-semibold text-ink-soft">{p.arriving}</b> covers arriving</span>
                      <span>·</span>
                      <span><b className="font-semibold text-ink-soft">{p.freeing}</b> table{p.freeing === 1 ? '' : 's'} freeing</span>
                      <span>·</span>
                      <span>{p.free === 0 ? 'no table free' : `${p.free} table${p.free === 1 ? '' : 's'} free`}{!past && p.free > 0 ? ' · room for more' : ''}</span>
                    </div>
                  );
                })()}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                {g.items.map(({ r, state }) => (
                  <BookingCard
                    key={r.id}
                    r={r}
                    state={state}
                    isToday={isToday}
                    isOpen={openId === r.id}
                    onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                    suggestion={suggestions[r.id]}
                    onSuggest={onSuggest}
                    onAcceptSuggestion={onAcceptSuggestion}
                    onEdit={onEdit}
                    onChanged={() => { setOpenId(null); onChanged(); }}
                  />
                ))}
              </div>
              </div>
            </div>
          </div>
        );
      })}

      {!nowPlaced && (
        <div ref={nowRef} className="flex items-center gap-3 py-1" data-testid="queue-now">
          <span className="w-14 shrink-0 text-right font-mono text-[11px] font-bold text-signal-stop tabular-nums">{currentTime}</span>
          <span className="flex-1 border-t-2 border-dashed border-signal-stop-line" />
        </div>
      )}

      {/* Walk-ins waiting — the same track, at the end */}
      {isToday && (
        <div className="flex gap-3 pt-4" data-testid="queue-waiting">
          <div className="w-14 shrink-0 text-right pt-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Waiting</span>
          </div>
          <div className="flex-1 min-w-0">
            <WaitlistPanel tables={tables} onRefresh={onChanged} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingQueue;
